import { DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { formatDate } from 'shared/dates';
import { isValid } from 'date-fns';
import { InfoRow } from '../../InfoRow';
import { FemaFloodHazardPoi, FemaRiskCategory } from '../PoiDialog.types';
import { EMPTY_DATA_VALUE } from '../../../../../../constants';

type TransKeys = { title: string; description: string };

const useStyles = makeStyles()((theme) => ({
  title: {
    padding: theme.spacing(2, 6, 1, 2),
    lineHeight: '1 !important',
  },
  content: {
    padding: theme.spacing(0, 2, 2, 2),
    maxWidth: 375,
    lineHeight: '18px',
  },
}));

const RISK_CONFIG: Record<string, TransKeys> = {
  regulatory_floodway: {
    title: 'map.poi.femaFloodHazard.regulatory.title',
    description: 'map.poi.femaFloodHazard.regulatory.description',
  },
  special_floodway: {
    title: 'map.poi.femaFloodHazard.regulatory.title',
    description: 'map.poi.femaFloodHazard.regulatory.description',
  },
  flood_hazard_1_pct: {
    title: 'map.poi.femaFloodHazard.onePct.title',
    description: 'map.poi.femaFloodHazard.onePct.description',
  },
  flood_hazard_0_2_pct: {
    title: 'map.poi.femaFloodHazard.zeroTwoPct.title',
    description: 'map.poi.femaFloodHazard.zeroTwoPct.description',
  },
  risk_due_to_levee: {
    title: 'map.poi.femaFloodHazard.levee.title',
    description: 'map.poi.femaFloodHazard.levee.description',
  },
  undetermined: {
    title: 'map.poi.femaFloodHazard.undetermined.title',
    description: 'map.poi.femaFloodHazard.undetermined.description',
  },
};

const getDialogTransKeys = (riskCategory: FemaRiskCategory): TransKeys => {
  return RISK_CONFIG[riskCategory] || RISK_CONFIG.undetermined;
};

export const FemaFloodHazardDialog = (
  props: FemaFloodHazardPoi,
): JSX.Element => {
  const { riskCategory, studyRevDate } = props;
  const { classes } = useStyles();
  const { t } = useTranslation();

  const { title, description } = getDialogTransKeys(riskCategory);
  const lastUpdated = isValid(studyRevDate) ? studyRevDate : undefined;

  return (
    <>
      <DialogTitle id="alert-dialog-title" className={classes.title}>
        <Typography
          component="span"
          variant="body2"
          sx={{
            fontWeight: 'bold',
          }}
        >
          {t(title)}
        </Typography>
      </DialogTitle>

      <DialogContent className={classes.content}>
        <Stack spacing={1}>
          <Typography variant="body2">{t(description)}</Typography>

          <InfoRow
            title={t('map.poi.femaFloodHazard.effectiveDate')}
            content={
              lastUpdated
                ? formatDate(lastUpdated, 'MMM d, yyyy')
                : EMPTY_DATA_VALUE
            }
          />
        </Stack>
      </DialogContent>
    </>
  );
};
